/**
 * Transforms folder data into a visual tree view, using the 'Treebeard' library
 *
 * This component abstracts away the implementation details of rendering the tree. It is a 'dumb'
 * component - e.g it provides the interfaces to allow for renaming, but does not actually implement
 * any of the functionality, as this component never has direct access to the source-of-truth folder
 * state.
 */

import React, { RefObject, createRef } from "react";
import { observer } from "mobx-react";
import { Treebeard, TreeNode } from "react-treebeard";
import { action, computed, observable } from "mobx";

import { FolderTreeViewStore, FolderTreeId } from "./FolderTreeViewStore";
import {
  customDecorators,
  customTheme,
  ITreebeardFolder,
  ISharedTreeProps,
  NameChangeFn,
  FolderChangeFn,
  RenameConfirmFn,
  FolderDragFn,
} from "./TreebeardDecorators";

interface IFolderTreeProps {
  viewStore: FolderTreeViewStore;

  // The maximum number of characters that can be entered into the 'renaming' input box.
  // This does not affect the rendering of any existing folder names
  maxNameLength: number;

  /**
   * Callback for when a particular folder is selected. Note that the <FolderTree> will not
   * mutate the selection state itself: this is to allow a selection event to do things outside
   * the context of this <FolderTree> (such as trigger other side-effects, do validation before
   * changing the selection state, etc).
   */
  onSelect: (id: FolderTreeId) => void;

  onDragStart?: FolderDragFn;
  onDragEnd?: () => void;

  onDropOnFolder?: FolderDragFn;

  // Callback for when the folder name is changed in rename mode
  // These rename functions are all optional, because:
  // - Not all use cases of <FolderTree> necessarily need rename functionality at all.
  // - There are many different ways of implementing renaming, not all of which require all these
  //   callback functions. e.g if there are confirm/cancel buttons outside the <FolderTree>, then
  //   we may not want the <FolderTree> to handle these actions internally.
  onNameChange?: NameChangeFn;

  // Callback for when a rename is confirmed (e.g when pressing Enter on the input box)
  onRenameConfirm?: RenameConfirmFn;

  // Callback for when a rename is cancelled (e.g when pressing Escape on the input box)
  onRenameCancel?: FolderChangeFn;

  onDeleteFolder?: FolderChangeFn;
  onCreateFolder?: FolderChangeFn;
  disableOptions?: boolean;
}

@observer
class FolderTree extends React.Component<IFolderTreeProps> {
  public static defaultProps = {
    // This is just a reasonable default number that is used by a lot of file systems
    maxNameLength: 256,
  };

  private treeParentRef: RefObject<HTMLDivElement> = createRef();

  @observable
  private folderBeingDragged: FolderTreeId | null = null;

  public render(): React.ReactElement {
    return (
      <div className="treebeard" ref={this.treeParentRef}>
        <Treebeard
          data={this.parsedFolderData}
          onToggle={this.onToggle}
          onSelect={this.onSelect}
          decorators={customDecorators}
          style={customTheme}
        />
      </div>
    );
  }

  @action.bound
  private onToggle(node: TreeNode): void {
    this.props.viewStore.onToggle(node.id as string);
  }

  @action.bound
  private onSelect(node: TreeNode): void {
    const { onSelect, viewStore } = this.props;
    const id = node.id as string;

    // A small quality-of-life tweak to make the folder toggle open/closed when it is selected,
    // but only if it's currently closed or already selected
    if (!viewStore.isFolderToggledOpen(id) || viewStore.isFolderSelected(id)) {
      this.onToggle(node);
    }

    onSelect(id);
  }

  @action.bound
  private handleDragStart(event: React.DragEvent, folderId: FolderTreeId): void {
    const { onDragStart } = this.props;

    if (!!onDragStart) {
      onDragStart(event, folderId);
      this.folderBeingDragged = folderId;
    }
  }

  @action.bound
  private handleDragEnd(): void {
    this.folderBeingDragged = null;

    if (this.props.onDragEnd) {
      this.props.onDragEnd();
    }
  }

  /**
   * Parse our tree data-structure (which uses a flat array) into the TreeBeard data-structure
   * (which uses nested objects).
   * Adapted from this nice solution for converting from flat > nested representations:
   * https://stackoverflow.com/questions/18017869/build-tree-array-from-flat-array-in-javascript
   *
   * Because this function is optimised for speed, it's not the prettiest to look at!
   * This is either O(nlog(n)) or O(n), depending on how the Map insert and lookup are implemented.
   *
   * @TODO (som, 2020-05-04): if this starts getting slow, there may be a way to break this up to
   * minimise how much needs to be recomputed. e.g the tree structure itself should rarely change,
   * and so we don't need to recalculate it every time a folder is selected or toggled.
   *
   * Like with most optimisations, doing this will almost certainly *add* complexity - so be mindful
   * about whether it's actually worth it! (I think we would need to use reactions and do our own
   * caching instead of using @computed functions, which would be messy...)
   */
  @computed
  private get parsedFolderData(): ITreebeardFolder[] {
    const {
      onDragStart,
      onDropOnFolder,
      onNameChange,
      onRenameConfirm,
      onRenameCancel,
      viewStore,
      maxNameLength,
      onDeleteFolder,
      onCreateFolder,
      disableOptions,
    } = this.props;
    const folders = viewStore.folders;

    // Temporary data structures for our optimisations
    const idMap = new Map<FolderTreeId, number>();
    const allTreebeardFolders: ITreebeardFolder[] = [];

    // The final tree structure we will return
    const roots: ITreebeardFolder[] = [];

    // Props that are shared between all folders in the tree
    const sharedProps: ISharedTreeProps = {
      onDragStart: this.handleDragStart,
      onDragEnd: this.handleDragEnd,
      onDropOnFolder,
      onNameChange,
      onRenameConfirm,
      onRenameCancel,
      maxNameLength,
      onDeleteFolder,
      onCreateFolder,
      disableOptions,
      treeParentRef: this.treeParentRef,
    };

    // First pass - convert each of our folders into individual Treebeard folders.
    // After this pass, each Treebeard folder will have all the data it needs *except* for the
    // tree structure itself.
    for (let i = 0; i < folders.length; ++i) {
      const folder = folders[i];

      // Map each folder id to it's position in the original list.
      // (This allows for constant-time lookups in the 2nd pass)
      idMap.set(folder.id, i);

      const toggled = viewStore.isFolderToggledOpen(folder.id);
      const selected = viewStore.isFolderSelected(folder.id);

      // Initialise a Treebeard folder object for each folder in the original list
      allTreebeardFolders.push({
        id: folder.id,
        name: folder.name,
        toggled,
        selected,
        renaming: folder.renaming || false,
        children: [],
        draggable: !!onDragStart,

        animationState: folder.animationState,
        isLoading: folder.isLoading,
        disableSelect: folder.disableSelect || false,
        isBeingDragged: false,

        ...sharedProps,
      });
    }

    // Second pass - build the nested tree structure.
    for (let i = 0; i < folders.length; ++i) {
      const folder = folders[i];

      if (!!folder.parent && idMap.has(folder.parent)) {
        // This particular folder has a parent, and that parent is in the list of folders supplied for this view.
        // @NOTE (som, 2019-12-13): it's important to check that the parent exists, because we cannot assume that
        // the parent folder is accessible by the user or visible in a particular folder view.

        // Add this Treebeard folder to it's parent's children
        const parentIndex = idMap.get(folder.parent) as number;
        allTreebeardFolders[parentIndex].children.push(allTreebeardFolders[i]);
      } else {
        // This folder has no parent in this view, which means we should render it as a root folder.
        roots.push(allTreebeardFolders[i]);
      }
    }

    // Possible third pass - if a folder is being dragged, tell all it's descendants that they are
    // being dragged
    if (this.folderBeingDragged && idMap.has(this.folderBeingDragged)) {
      const treebeardFolderBeingDragged = allTreebeardFolders[idMap.get(this.folderBeingDragged) as number];
      addBeingDraggedFlag(treebeardFolderBeingDragged);
    }

    return roots;
  }
}

/**
 * Tell `treebeardFolderBeingDragged` and all it's descendants that they are being dragged.
 */
function addBeingDraggedFlag(treebeardFolderBeingDragged: ITreebeardFolder): void {
  // We'll do a standard tree traversal using a queue, starting with the folder being dragged
  const traversalQueue = [treebeardFolderBeingDragged];

  while (traversalQueue.length > 0) {
    const nextTreebeardFolder = traversalQueue.pop() as ITreebeardFolder;

    // You're being dragged!
    nextTreebeardFolder.isBeingDragged = true;

    // Push all the immediate sub-folders to the queue, allowing us to traverse down the tree
    // in later loops.
    for (const child of nextTreebeardFolder.children) {
      traversalQueue.push(child);
    }
  }
}

export { ITreebeardFolder, FolderTree };
